import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const radiusFor = (users, max) => 4 + Math.sqrt(users / max) * 22;
const nz = new Intl.NumberFormat("en-NZ");

export default function UserWorldMap({ clusters = [], onHover, hovered }) {
    const max = Math.max(1, ...clusters.map((c) => c.users));
    return (
        <div className="relative w-full h-full" data-testid="user-world-map">
            <ComposableMap
                projection="geoNaturalEarth1"
                projectionConfig={{ scale: 175, center: [20, 5] }}
                width={980}
                height={500}
                style={{ width: "100%", height: "100%" }}
            >
                <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                        geographies
                            .filter((g) => g.properties.name !== "Antarctica")
                            .map((geo) => (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    style={{
                                        default: {
                                            fill: "#0d0d0d",
                                            stroke: "rgba(34,197,94,0.22)",
                                            strokeWidth: 0.4,
                                            outline: "none",
                                        },
                                        hover: { fill: "#111", outline: "none" },
                                        pressed: { fill: "#111", outline: "none" },
                                    }}
                                />
                            ))
                    }
                </Geographies>

                {[...clusters]
                    .sort((a, b) => b.users - a.users)
                    .map((c) => {
                        const r = radiusFor(c.users, max);
                        const isHovered = hovered === c.region;
                        const core = c.country === "NZ" || c.country === "AU";
                        const color = core ? "#22C55E" : "#15803D";
                        return (
                            <Marker
                                key={c.region}
                                coordinates={[c.lng, c.lat]}
                                onMouseEnter={() => onHover?.(c.region)}
                                onMouseLeave={() => onHover?.(null)}
                                data-testid={`user-cluster-${c.code.toLowerCase()}`}
                            >
                                <circle
                                    r={r + 5}
                                    fill={color}
                                    fillOpacity={isHovered ? 0.28 : 0.12}
                                    style={{ transition: "fill-opacity 160ms ease" }}
                                />
                                <circle
                                    r={r}
                                    fill={color}
                                    fillOpacity={0.85}
                                    stroke="#050505"
                                    strokeWidth={1}
                                    style={{
                                        filter: `drop-shadow(0 0 ${isHovered ? 14 : 6}px ${color})`,
                                        transition: "filter 160ms ease",
                                    }}
                                />
                                {r > 9 && (
                                    <text
                                        textAnchor="middle"
                                        y={3}
                                        style={{
                                            fontFamily: "JetBrains Mono, monospace",
                                            fontSize: 8,
                                            fill: "#050505",
                                            fontWeight: 700,
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {c.users >= 1000 ? `${(c.users / 1000).toFixed(1)}k` : c.users}
                                    </text>
                                )}
                            </Marker>
                        );
                    })}
            </ComposableMap>

            <div className="absolute bottom-2 right-2 flex items-center gap-3 hg-glass px-3 py-2 rounded-sm">
                <span className="hg-eyebrow text-[9px]">Cluster size</span>
                {[200, 1500, 4800].map((v) => (
                    <div key={v} className="flex items-center gap-1.5">
                        <span
                            className="rounded-full bg-[#22C55E]"
                            style={{
                                width: radiusFor(v, max) / 1.6,
                                height: radiusFor(v, max) / 1.6,
                                boxShadow: "0 0 6px #22C55E",
                            }}
                        />
                        <span className="font-mono-data text-[10px] text-[#A1A1AA] tracking-widest">
                            {nz.format(v)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
